import React, { createContext, useCallback, useState, useContext, useEffect } from 'react';

import api from '../services/api';

import i18n from '../i18n';

interface User {
	id: number;
	name: string;
	email: string;
	phone: string;
}

interface AuthState {
	token: string;
	user: User;
}

interface Subscriptions {
	id: number;
	profile: string;
	balance_likes: number;
	balance_views: number;
	status: string;
	type_service: string;
	name: string;
	description: string;
	payment: string;
	expiration_date: Date;
}

interface Balance {
	balance: number;
}

interface Posts {
	shortcode: string;
	uri: string;
}

interface Profile {
	username: string;
	fullName: string;
	biography: string;
	profile: string;
	posts: Posts[];
	postsFeed: number;
	followers: number;
	following: number;
}

interface IProfileSelected {
	subscriptionId: number;
	profile: string;
}

interface SignInCredentials {
	email: string;
	password: string;
}

interface AuthContextData {
	user: User;
	subscriptions: Subscriptions[];
	profile: Profile;
	signIn: (credentials: SignInCredentials) => Promise<void>;
	signOut: () => void;
	handleSelectedProfile: (id: number, profile: string) => Promise<void>;
	handleUpdateProfileSubscription: (subscriptionId: number, profile: string) => Promise<boolean | undefined>;
	loading: boolean;
	noProfile: boolean;
	handleSetNoProfile: () => void;
	handleUploadPhotos: (profile: string, id: number) => Promise<void>;
	balance: Balance;
	handleGetBalanceUser: () => {};
	profileSelected: IProfileSelected;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {

	const [subscriptions, setSubscriptions] = useState<Subscriptions[]>([]);
	const [profile, setProfile] = useState<Profile>({} as Profile);
	const [loading, setLoading] = useState<boolean>(false);
	const [noProfile, setNoProfile] = useState<boolean>(false);
	const [balance, setBalance] = useState<Balance>({} as Balance);
	const [profileSelected, setProfileSelected] = useState<IProfileSelected>({} as IProfileSelected);

	const [data, setData] = useState<AuthState>(() => {

		const lang = i18n.language === 'es' ? 'tbes' : 'tb';

		const token = localStorage.getItem(`@${lang}:token`);
		const user = localStorage.getItem(`@${lang}:user`);

		if (token && user) {

			api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

			api.interceptors.response.use(
				(res) => res,

				async (err) => {
					console.log("Interceptor response error");

					if (err.response.status === 401) {

						localStorage.removeItem(`@${lang}:token`);
						localStorage.removeItem(`@${lang}:user`);

						setData({} as AuthState);
					}

					return Promise.reject(err);
				},
			);

			return { token, user: JSON.parse(user) }
		}

		return {} as AuthState;
	});

	async function handleGetBalanceUser() {

		const { data } = await api.get('/users/balance');

		setBalance(data);
	}

	useEffect(() => {

		if (!data.user) return;

		handleGetSubscriptions();

		handleGetSubscriptionProfileSelected();

		handleGetBalanceUser();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	async function handleGetSubscriptions() {

		const { data } = await api.get('/subscriptions');

		setSubscriptions(data);
	}

	const handleSelectedProfile = useCallback(async (id, profile) => {

		try {
			setLoading(true);

			const response = await api.get(`/profile/${profile}`);

			setProfile(response.data.user);

			await api.post(`/subscriptions/profile-selected/${id}`);

			handleUpdateIsPrivate(id, response.data.user.profile);

			setLoading(false);
		} catch (error: any) {

			if (error.response.data.message) {
				setLoading(false);
				setNoProfile(true);
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function handleGetSubscriptionProfileSelected() {

		const profileSelected = await api.get('/subscriptions/profile-selected');

		const { subscriptionId, profile } = profileSelected.data;

		setProfileSelected({ subscriptionId, profile });

		if (!profile) return;

		handleUploadPhotos(profile, subscriptionId);

		const dataProfileSelected = await api.get(`/profile/${profile}`);

		setProfile(dataProfileSelected.data.user);
	}

	const handleUploadPhotos = useCallback(async (username, id) => {

		setLoading(true);

		const photos = await api.post(`/profile/update/${username}`);

		setProfile(photos.data.user);

		handleUpdateIsPrivate(id, photos.data.user.profile);

		setLoading(false);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleUpdateIsPrivate = useCallback(async (id, profilePicUrl) => {

		await api.put(`/subscriptions/update-is-private`, { id, profilePicUrl });

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleUpdateProfileSubscription = useCallback(async (subscriptionId, profile) => {

		const response = await api.put('/subscriptions/update-profile', {
			subscriptionId,
			profile
		});

		if (response.status === 200) {

			handleGetSubscriptionProfileSelected();

			handleGetSubscriptions();

			return true;
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const signIn = useCallback(async ({ email, password }) => {

		const lang = i18n.language === 'es' ? 'tbes' : 'tb';

		const response = await api.post('/auth', {
			email,
			password
		}, {
			headers: {
				company: lang
			}
		});

		const { token, user } = response.data;

		localStorage.setItem(`@${lang}:token`, token);
		localStorage.setItem(`@${lang}:user`, JSON.stringify(user));

		api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

		setData({ token, user });
	}, []);

	const signOut = useCallback(() => {

		const lang = i18n.language === 'es' ? 'tbes' : 'tb';

		localStorage.removeItem(`@${lang}:token`);
		localStorage.removeItem(`@${lang}:user`);

		setData({} as AuthState);
		setProfile({} as Profile);
	}, []);

	// Verify function
	const handleSetNoProfile = () => {

		setNoProfile(false);
	}

	return (
		<AuthContext.Provider value={
			{
				user: data.user,
				subscriptions,
				profile,
				signIn,
				signOut,
				handleSelectedProfile,
				handleUpdateProfileSubscription,
				loading,
				noProfile,
				handleSetNoProfile,
				handleUploadPhotos,
				balance,
				handleGetBalanceUser,
				profileSelected
			}
		}>
			{children}
		</AuthContext.Provider>
	);
}

function useAuth(): AuthContextData {
	const context = useContext(AuthContext);

	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
}

export { AuthProvider, useAuth }